// Furnace block entity + smelting state machine (M6.2). PURE (ENG-1).
//
// Three slots — input, fuel, output — and four timers. Each tick: keep burning if
// there is fuel and something to smelt, advance the cook toward the recipe's time
// (200 gt default), and on completion move one result into the output while
// banking its XP (released when a player collects the output). Slot mutations for
// the UI go through the `Slots` adapter so the container window and every
// transaction op operate on it unchanged (PLR-1).
import { type ItemStack, type Slots, emptyStack, isEmpty, canStack } from '../inventory';
import { smeltResult, fuelTicks, isFuel, type CookKind } from '../smelting';
import type { Registries } from '../../registry/load';

/** A furnace block entity's persisted state (three slots + burn/cook progress). */
export interface FurnaceData {
  input: ItemStack;
  fuel: ItemStack;
  output: ItemStack;
  /** Ticks the current fuel unit has left to burn (0 = not burning). */
  burnTime: number;
  /** Ticks the current fuel unit burned for (drives the flame gauge). */
  burnTotal: number;
  /** Ticks the current item has cooked. */
  cookTime: number;
  /** Ticks the current smelt needs (recipe time; 0 when idle). */
  cookTotal: number;
  /** XP banked from completed smelts, released on output collect. */
  xp: number;
}

export const FURNACE_INPUT = 0;
export const FURNACE_FUEL = 1;
export const FURNACE_OUTPUT = 2;

export function newFurnace(): FurnaceData {
  return { input: emptyStack(), fuel: emptyStack(), output: emptyStack(), burnTime: 0, burnTotal: 0, cookTime: 0, cookTotal: 0, xp: 0 };
}

/** Non-empty stacks a broken furnace scatters (input + fuel + output). */
export function furnaceContents(f: FurnaceData): ItemStack[] {
  return [f.input, f.fuel, f.output].filter((s) => !isEmpty(s));
}

/** Can `result` be merged into the current output slot? */
function outputAccepts(output: ItemStack, result: ItemStack, maxStack: number): boolean {
  if (isEmpty(output)) return true;
  return canStack(output, result) && output.count + result.count <= maxStack;
}

/**
 * Advance one furnace by one game tick. Returns true if any visible state changed
 * (so the host can flag the block entity dirty). `kinds` selects the recipe family
 * (a blast furnace/smoker would pass its own — the plain furnace uses smelting).
 */
export function tickFurnace(
  f: FurnaceData,
  reg: Registries,
  maxStackOf: (item: number) => number,
  kinds: readonly CookKind[] = ['smelting'],
): boolean {
  const before = f.burnTime | 0;
  const beforeCook = f.cookTime;
  const wasLit = f.burnTime > 0;

  if (f.burnTime > 0) f.burnTime--;

  const recipe = smeltResult(reg, f.input, kinds);
  const resultStack: ItemStack | null = recipe ? { item: recipe.item, count: 1 } : null;
  const canSmelt = !!(resultStack && outputAccepts(f.output, resultStack, maxStackOf(resultStack.item)));

  // Light a fresh fuel unit when we have something to smelt and no flame left.
  if (f.burnTime === 0 && canSmelt && !isEmpty(f.fuel)) {
    const t = fuelTicks(reg, f.fuel.item);
    if (t > 0) {
      f.burnTime = t;
      f.burnTotal = t;
      f.fuel = f.fuel.count <= 1 ? emptyStack() : { ...f.fuel, count: f.fuel.count - 1 };
    }
  }

  if (f.burnTime > 0 && canSmelt && recipe && resultStack) {
    f.cookTotal = recipe.time;
    f.cookTime++;
    if (f.cookTime >= f.cookTotal) {
      // Complete one smelt: bank XP, add to output, consume one input.
      f.output = isEmpty(f.output)
        ? { item: resultStack.item, count: 1 }
        : { ...f.output, count: f.output.count + 1 };
      f.input = f.input.count <= 1 ? emptyStack() : { ...f.input, count: f.input.count - 1 };
      f.xp += recipe.xp;
      f.cookTime = 0;
    }
  } else {
    // Idle / stalled: the cook progress decays back toward 0 (MC behaviour).
    if (f.cookTime > 0) f.cookTime = Math.max(0, f.cookTime - 2);
    if (!canSmelt) f.cookTotal = f.cookTime > 0 ? f.cookTotal : 0;
  }

  const nowLit = f.burnTime > 0;
  return before !== f.burnTime || beforeCook !== f.cookTime || wasLit !== nowLit;
}

/**
 * Collect (and zero) the furnace's banked XP — called when a player takes the
 * output. Returns the whole XP to award (fractional remainder is kept).
 */
export function collectFurnaceXp(f: FurnaceData): number {
  const whole = Math.floor(f.xp);
  f.xp -= whole;
  return whole;
}

/** A `Slots` view over a furnace for the container window (3 slots). */
export function furnaceSlots(f: FurnaceData, reg: Registries, maxStackOf: (item: number) => number): Slots {
  return {
    size: 3,
    get: (i) => (i === FURNACE_INPUT ? f.input : i === FURNACE_FUEL ? f.fuel : f.output),
    set: (i, s) => {
      if (i === FURNACE_INPUT) f.input = s;
      else if (i === FURNACE_FUEL) f.fuel = s;
      else f.output = s;
    },
    maxStackOf: (item) => maxStackOf(item),
    accepts: (i, s) => {
      if (isEmpty(s)) return true;
      if (i === FURNACE_OUTPUT) return false; // take-only
      if (i === FURNACE_FUEL) return isFuel(reg, s);
      return true; // input accepts anything (non-smeltables just never cook)
    },
  };
}
